import React from 'react';
import { Trans } from 'react-i18next';
import i18next from 'i18next';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import { Tag, TagType, Size } from '@signaturit/react-components-library';
import {
    Avatar,
    ButtonBase,
    Button,
    ClickAwayListener,
    Divider,
    Grow,
    MenuItem,
    MenuList,
    Paper,
    Popper,
} from '@material-ui/core';

import './UserMenu.scss';
import Gauges from '../../../../../components/Gauges/Gauges';
import { User } from '../../../../../../models/User';

export enum UserMenuTypeEnum {
    BUTTON = 'button',
    ANCHOR = 'anchor',
    GAUGES = 'gauges',
}

export enum UserMenuAlignEnum {
    NONE = 'none',
    CENTER = 'center',
}

export interface UserMenuConfig {
    link: string | undefined;
    name: string;
    type: UserMenuTypeEnum;
    withDivider: boolean;
    align: UserMenuAlignEnum;
    visible: boolean;
    newTab?: boolean;
    classes?: string;
    icon?: string;
    showTag?: boolean;
}

export interface UserMenuProps {
    user: User;
    config: UserMenuConfig[];
}

export const UserMenu = ({ user, config }: UserMenuProps) => {
    const EVENT_KEYS = {
        TAB: 'Tab',
    };

    const TARGET_OPTIONS = {
        BLANK: '_blank',
        SELF: '_self',
    };

    if (!user || !user.firstName) {
        return null;
    }
    const fullName = `${user.firstName} ${user.lastName}`;
    const [open, setOpen] = React.useState(false);
    const anchorRef = React.useRef<HTMLButtonElement>(null);

    const handleToggle = () => {
        const newState = !open;
        setOpen(newState);
    };

    const handleClose = (event: any) => {
        if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
            return;
        }

        setOpen(false);
    };

    const handleListKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === EVENT_KEYS.TAB) {
            event.preventDefault();
            setOpen(false);
        }
    };

    const getItemTemplate = (item: UserMenuConfig, className: string) => (
        <div className={className}>
            {item.type === UserMenuTypeEnum.ANCHOR ? (
                <label>
                    <Trans>{i18next.t(item.name)}</Trans>
                    {item.showTag && (
                        <div className="user-menu-component__menu-section__tag-container">
                            <Tag type={TagType.marketing} size={Size.small}>
                                {i18next.t('default.menus._left.new')}
                            </Tag>
                        </div>
                    )}
                </label>
            ) : (
                <Button variant="outlined" size="small" color="primary">
                    <Trans>{i18next.t(item.name)}</Trans>
                </Button>
            )}
            {item.icon && <i className={`icon ${item.icon}`} />}
        </div>
    );

    return (
        <div className="user-menu-component" onClick={handleToggle}>
            <Avatar className="user-menu-component__avatar" alt={fullName} src={user.avatar} />
            <span className="user-menu-component__name-section">{fullName} </span>
            <ButtonBase
                ref={anchorRef}
                disableRipple={true}
                aria-haspopup="true"
                onClick={handleToggle}
                aria-controls={open ? 'menu-list-grow' : undefined}
            >
                {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
            </ButtonBase>
            <Popper
                className="user-menu-component__popper-section"
                open={open}
                anchorEl={anchorRef.current}
                role={undefined}
                placement="bottom-end"
                transition
                disablePortal
            >
                {({ TransitionProps }) => (
                    <Grow {...TransitionProps}>
                        <Paper>
                            <ClickAwayListener onClickAway={handleClose}>
                                <MenuList
                                    className="user-menu-component__menu-section"
                                    autoFocusItem={open}
                                    id="menu-list-grow"
                                    onKeyDown={handleListKeyDown}
                                >
                                    {config.map(menuItem => (
                                        <div key={menuItem.name}>
                                            {menuItem.type === UserMenuTypeEnum.GAUGES && menuItem.visible ? (
                                                <div className={menuItem.classes}>
                                                    <Divider />
                                                    <MenuItem>
                                                        <Gauges />
                                                    </MenuItem>
                                                </div>
                                            ) : (
                                                menuItem.visible && (
                                                    <div className={menuItem.classes ? menuItem.classes : ''}>
                                                        {menuItem.withDivider && <Divider />}
                                                        <a
                                                            href={menuItem.link}
                                                            target={
                                                                menuItem.newTab
                                                                    ? TARGET_OPTIONS.BLANK
                                                                    : TARGET_OPTIONS.SELF
                                                            }
                                                        >
                                                            <MenuItem onClick={handleClose}>
                                                                <div
                                                                    className={`
                                                                user-menu-component__menu-section--item-style
                                                            `}
                                                                >
                                                                    {menuItem.icon
                                                                        ? getItemTemplate(
                                                                              menuItem,
                                                                              'user-menu-component__menu-section__item-content'
                                                                          )
                                                                        : getItemTemplate(
                                                                              menuItem,
                                                                              `user-menu-component__menu-section--align-${menuItem.align}`
                                                                          )}
                                                                </div>
                                                            </MenuItem>
                                                        </a>
                                                    </div>
                                                )
                                            )}
                                        </div>
                                    ))}
                                </MenuList>
                            </ClickAwayListener>
                        </Paper>
                    </Grow>
                )}
            </Popper>
        </div>
    );
};
